import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumnDef } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import RecipeEditor from "@/features/inventory/RecipeEditor";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { InvRecipeId, InvResourceClassEnum } from "@brewbase/db/types";
import { EditIcon, Trash2 } from "lucide-react";

type RecipeRow = RouterOutputs["inventory"]["recipe"]["list"][number];

export const Route = createFileRoute("/inventory/recipes/")({
  component: RecipesPage,
});

function RecipesPage() {
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [modalRecipeId, setModalRecipeId] = useState<InvRecipeId | null>(null);

  const { data: recipes = [] } = trpc.inventory.recipe.list.useQuery();
  const { data: categories = [] } =
    trpc.inventory.recipe.listCategories.useQuery();
  const [categoryFilter, setCategoryFilter] = useState<number | "ALL">("ALL");
  const createMinimal = trpc.inventory.recipe.createMinimal.useMutation({
    onSuccess: (r) => {
      utils.inventory.recipe.list.invalidate();
      // Open editor modal immediately
      setModalRecipeId(r!.id as InvRecipeId);
      setIsCreateOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });
  const deleteRecipe = trpc.inventory.recipe.delete.useMutation({
    onSuccess: () => {
      utils.inventory.recipe.list.invalidate();
      toast.success("Recipe deleted");
    },
    onError: (e) => toast.error(e.message),
  });

  const columns = useMemo<DataTableColumnDef<RecipeRow, any>[]>(
    () => [
      { id: "name", header: "Name", accessorKey: "name", cellTypeId: "string" },
      {
        id: "version",
        header: "Version",
        accessorKey: "version",
        cellTypeId: "string",
      },
      {
        id: "produces_resource_name",
        header: "Output Resource",
        accessorKey: "produces_resource_name",
        cellTypeId: "string",
      },
      {
        id: "category_name",
        header: "Category",
        accessorKey: "category_name",
        cellTypeId: "string",
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setModalRecipeId(row.original.id)}
            >
              <EditIcon className="h-4 w-4" />
              <span className="sr-only">Edit</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              disabled={deleteRecipe.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Are you sure you want to delete supplier "${row.original.name}"?`,
                  )
                ) {
                  deleteRecipe.mutate(row.original.id);
                }
              }}
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">Delete</span>
            </Button>
          </div>
        ),
      },
    ],
    [navigate],
  );

  return (
    <div className="gap-4 flex flex-col grow min-h-0 h-full">
      <DataTable
        leftCustomToolbar={
          <div className="flex items-center gap-2">
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button>New Recipe</Button>
              </DialogTrigger>
              <DialogContent className="max-w-none w-[min(90vw,920px)]">
                <DialogHeader>
                  <DialogTitle>Create Recipe</DialogTitle>
                </DialogHeader>
                <CreateRecipeForm
                  onSubmit={(v) => createMinimal.mutate(v as any)}
                  isSubmitting={createMinimal.isPending}
                />
              </DialogContent>
            </Dialog>

            <div className="flex items-center gap-2">
              <Select
                value={String(categoryFilter)}
                onValueChange={(v) =>
                  setCategoryFilter(v === "ALL" ? "ALL" : Number(v))
                }
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        }
        data={recipes.filter((r) =>
          categoryFilter === "ALL" ? true : r.category_id === categoryFilter,
        )}
        columns={columns}
        searchPlaceholder="Search recipes..."
        maxHeight="none"
        className="grow min-h-0 flex-1"
      />

      {modalRecipeId && (
        <Dialog
          open={!!modalRecipeId}
          onOpenChange={(o) => !o && setModalRecipeId(null)}
        >
          <DialogContent className="max-w-none w-[min(90vw,920px)]">
            <RecipeEditor
              recipeId={modalRecipeId}
              onClose={() => setModalRecipeId(null)}
              onOpenFullPage={() =>
                navigate({
                  to: "/inventory/recipes/$recipeId",
                  params: { recipeId: modalRecipeId },
                })
              }
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function CreateRecipeForm({
  onSubmit,
  isSubmitting,
}: {
  onSubmit: (v: {
    name: string;
    version: string;
    resource_class?: InvResourceClassEnum;
  }) => void;
  isSubmitting?: boolean;
}) {
  const [name, setName] = useState("");
  const [version, setVersion] = useState("");
  const [resourceClass, setResourceClass] = useState<
    InvResourceClassEnum | undefined
  >(InvResourceClassEnum.INGREDIENT);
  return (
    <form
      className="grid gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ name, version, resource_class: resourceClass });
      }}
    >
      <div>
        <Label>Name</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., 1M Tris-HCl Buffer"
          required
        />
      </div>
      <div>
        <Label>Version</Label>
        <Input
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          placeholder="v1.0"
          required
        />
      </div>
      <div>
        <Label>Resource Class</Label>
        <Select
          value={resourceClass}
          onValueChange={(v) => setResourceClass(v as any)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(InvResourceClassEnum).map((val) => (
              <SelectItem key={val} value={val as any}>
                {val}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <Button type="submit" disabled={isSubmitting}>
          Create & Edit
        </Button>
      </div>
    </form>
  );
}
